// src/components/module/reportes/Tables/ReportesDataTable.tsx
import React, { useState, useContext } from 'react';
import { DataGrid, GridColDef, GridToolbar, esES } from '@mui/x-data-grid';
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Visibility,
  GetApp,
  FilterList,
} from '@mui/icons-material';
import { DarkModeContext } from '../../../../context/darkModeContext';
import { ReporteVentasDetalle } from '../../../types/reportes.types';
import { ReportesApi } from '../../../../services/api/reportesApi';
import './reportesDataTable.scss';
interface ReportesDataTableProps {
  data: ReporteVentasDetalle[];
  titulo?: string;
  onVerDetalle?: (id: number) => void;
  onExportar?: () => void;
  loading?: boolean;
}
export const ReportesDataTable: React.FC<ReportesDataTableProps> = ({
  data,
  titulo = 'Reporte de Ventas',
  onVerDetalle,
  onExportar,
  loading = false,
}) => {
  const { darkMode } = useContext(DarkModeContext);
  const [pageSize, setPageSize] = useState<number>(10);
  const columns: GridColDef[] = [
    {
      field: 'CodigoFacturaVenta',
      headerName: 'Factura',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color="primary"
          variant="outlined"
        />
      ),
    },
    {
      field: 'FechaVenta',
      headerName: 'Fecha',
      width: 120,
      valueFormatter: (params) => {
        return new Date(params.value as string).toLocaleDateString('es-CO');
      },
    },
    {
      field: 'Cliente',
      headerName: 'Cliente',
      width: 200,
      flex: 1,
    },
    {
      field: 'Usuario',
      headerName: 'Vendedor',
      width: 150,
    },
    {
      field: 'Subtotal',
      headerName: 'Subtotal',
      width: 130,
      type: 'number',
      valueFormatter: (params) => {
        return ReportesApi.formatearMoneda(params.value as number);
      },
    },
    {
      field: 'Impuesto',
      headerName: 'Impuesto',
      width: 130,
      type: 'number',
      valueFormatter: (params) => {
        return ReportesApi.formatearMoneda(params.value as number);
      },
    },
    {
      field: 'Total',
      headerName: 'Total',
      width: 130,
      type: 'number',
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600} color="primary.main">
          {ReportesApi.formatearMoneda(params.value as number)}
        </Typography>
      ),
    },
    {
      field: 'EstadoVenta',
      headerName: 'Estado',
      width: 120,
      renderCell: (params) => {
        const estado = params.value as string;
        let color: 'success' | 'warning' | 'error' | 'default' = 'default';
        if (estado === 'Pagada') color = 'success';
        else if (estado === 'Pendiente') color = 'warning';
        else if (estado === 'Anulada') color = 'error';
        return (
          <Chip
            label={estado}
            size="small"
            color={color}
            sx={{ fontWeight: 600 }}
          />
        );
      },
    },
    {
      field: 'Origen',
      headerName: 'Origen',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          variant="outlined"
          sx={{
            borderColor: params.value === 'ACTUAL' ? '#10b981' : '#64748b',
            color: params.value === 'ACTUAL' ? '#10b981' : '#64748b',
          }}
        />
      ),
    },
    {
      field: 'acciones',
      headerName: 'Acciones',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Box>
          {onVerDetalle && (
            <Tooltip title="Ver detalle">
              <IconButton
                size="small"
                onClick={() => onVerDetalle(params.row.Id)}
                color="primary"
              >
                <Visibility fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];
  return (
    <Paper className="reportes-datatable" elevation={2}>
        <Box className="datatable-header">
          <Typography variant="h6" fontWeight={600}>
            {titulo}
          </Typography>
          <Box className="datatable-actions">
            {onExportar && (
              <Tooltip title="Exportar">
                <IconButton onClick={onExportar} color="primary">
                  <GetApp />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
        <DataGrid
          rows={data}
          columns={columns}
          pageSize={pageSize}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[5, 10, 20, 50, 100]}
          pagination
          checkboxSelection={false}
          disableSelectionOnClick
          autoHeight
          loading={loading}
          getRowId={(row) => row.Id}
          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
          components={{
            Toolbar: GridToolbar,
          }}
          componentsProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 },
              printOptions: { disableToolbarButton: true },
              csvOptions: { 
                fileName: `reporte-ventas-${new Date().toISOString().split('T')[0]}`,
                utf8WithBom: true,
              },
            },
          }}
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: darkMode
                ? 'rgba(8, 145, 178, 0.08)'
                : 'rgba(8, 145, 178, 0.04)',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: darkMode ? '#0f172a' : '#f8fafc',
              borderBottom: '2px solid',
              borderColor: darkMode ? '#334155' : '#e2e8f0',
              fontWeight: 600,
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 600,
            },
            '& .MuiDataGrid-toolbarContainer': {
              padding: '16px',
              gap: '12px',
              borderBottom: '1px solid',
              borderColor: darkMode ? '#334155' : '#e2e8f0',
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid',
              borderColor: darkMode ? '#334155' : '#e2e8f0',
            },
          }}
        />
        {data.length === 0 && !loading && (
          <Box className="datatable-empty-state">
            <FilterList sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No hay datos disponibles
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ajusta los filtros para ver los resultados
            </Typography>
          </Box>
        )}
      </Paper>
  );
};